import React, { useState, useEffect } from 'react';
import { 
  FaChartLine, 
  FaSpinner, 
  FaTrophy, 
  FaCalendarAlt, 
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaBook,
  FaUsers,
  FaClock,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaGraduationCap,
  FaAward,
  FaBullseye,
  FaFlag
} from 'react-icons/fa';
import axios from 'axios';
import config from '../config/api';

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${config.apiURL}/guardian/analytics`);
        setAnalyticsData(response.data);
      } catch (err) {
        console.error('Erro ao carregar dados analíticos:', err);
        setError('Erro ao carregar dados analíticos');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getImprovementIcon = (improvement) => {
    if (improvement > 0) return <FaArrowUp className="text-green-500" />;
    if (improvement < 0) return <FaArrowDown className="text-red-500" />;
    return <FaMinus className="text-gray-500" />;
  };

  const getImprovementColor = (improvement) => {
    if (improvement > 0) return 'text-green-600';
    if (improvement < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning':
        return <FaExclamationTriangle className="text-yellow-500" />;
      case 'danger':
        return <FaExclamationTriangle className="text-red-500" />;
      case 'info':
        return <FaInfoCircle className="text-blue-500" />;
      default:
        return <FaInfoCircle className="text-gray-500" />;
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'danger':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getGradeColor = (grade) => {
    if (grade >= 9) return 'text-green-600';
    if (grade >= 7) return 'text-blue-600';
    if (grade >= 5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getGradeIcon = (grade) => {
    if (grade >= 9) return <FaTrophy className="text-yellow-500" />;
    if (grade >= 7) return <FaAward className="text-blue-500" />;
    if (grade >= 5) return <FaBullseye className="text-yellow-500" />;
    return <FaFlag className="text-red-500" />;
  };

  const getPunctualityLevel = (rate) => {
    if (rate >= 90) return { level: 'Excelente', color: 'text-green-600', icon: <FaTrophy className="text-green-500" /> };
    if (rate >= 80) return { level: 'Bom', color: 'text-blue-600', icon: <FaAward className="text-blue-500" /> };
    if (rate >= 70) return { level: 'Regular', color: 'text-yellow-600', icon: <FaBullseye className="text-yellow-500" /> };
    return { level: 'Precisa Melhorar', color: 'text-red-600', icon: <FaFlag className="text-red-500" /> };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando relatórios...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <FaExclamationTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Erro ao carregar dados
            </h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData || 
      ((!analyticsData.performance_over_time || analyticsData.performance_over_time.length === 0) && 
      (!analyticsData.performance_by_subject || Object.keys(analyticsData.performance_by_subject).length === 0))) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <FaChartLine className="text-blue-600" />
              <span>Relatórios e Análises</span>
            </h1>
            <p className="mt-2 text-gray-600">
              Acompanhe o desempenho e progresso educacional
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FaGraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Dados Insuficientes
            </h3>
            <p className="text-gray-600 mb-4">
              Relatórios detalhados aparecerão aqui quando houver mais dados de lições e avaliações.
            </p>
            <p className="text-sm text-gray-500">
              Complete algumas lições para ver análises detalhadas do desempenho.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const punctualityInfo = getPunctualityLevel(analyticsData.punctuality_rate);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FaChartLine className="text-blue-600" />
            <span>Relatórios e Análises</span>
          </h1>
          <p className="mt-2 text-gray-600">
            Acompanhe o desempenho e progresso educacional detalhado
          </p>
        </div>

        {/* Alertas */}
        {analyticsData.alerts && analyticsData.alerts.length > 0 && (
          <div className="mb-8 space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Alertas e Recomendações</h2>
            {analyticsData.alerts.map((alert, index) => (
              <div key={index} className={`p-4 rounded-lg border flex items-start space-x-3 ${getAlertColor(alert.type)}`}>
                {getAlertIcon(alert.type)}
                <p className="font-medium">{alert.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* Resumo Geral */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Média Atual</p>
                <p className={`text-2xl font-bold ${getGradeColor(analyticsData.current_month_average)}`}>
                  {analyticsData.current_month_average || '-'}
                </p>
              </div>
              {getGradeIcon(analyticsData.current_month_average)}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pontualidade</p>
                <p className={`text-2xl font-bold ${punctualityInfo.color}`}>
                  {analyticsData.punctuality_rate}%
                </p>
                <p className={`text-xs ${punctualityInfo.color}`}>
                  {punctualityInfo.level}
                </p>
              </div>
              {punctualityInfo.icon}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Evolução</p>
                <div className="flex items-center space-x-2">
                  <p className={`text-2xl font-bold ${getImprovementColor(analyticsData.improvement)}`}>
                    {analyticsData.improvement > 0 ? '+' : ''}{analyticsData.improvement || 0}
                  </p>
                  {getImprovementIcon(analyticsData.improvement)}
                </div>
              </div>
              <FaChartLine className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Mês Anterior</p>
                <p className={`text-2xl font-bold ${getGradeColor(analyticsData.previous_month_average)}`}>
                  {analyticsData.previous_month_average || '-'}
                </p>
              </div>
              <FaCalendarAlt className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Desempenho por Matéria */}
        {analyticsData.performance_by_subject && Object.keys(analyticsData.performance_by_subject).length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <FaBook className="text-blue-600" />
              <span>Desempenho por Matéria</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(analyticsData.performance_by_subject).map(([subject, data]) => (
                <div key={subject} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{subject}</h3>
                    {getGradeIcon(data.average)}
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Média:</span>
                      <span className={`font-bold ${getGradeColor(data.average)}`}>
                        {data.average.toFixed(1)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Avaliações:</span>
                      <span className="text-sm text-gray-900">{data.count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div 
                        className={`h-2 rounded-full ${
                          data.average >= 9 ? 'bg-green-500' :
                          data.average >= 7 ? 'bg-blue-500' :
                          data.average >= 5 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(data.average * 10, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Histórico de Desempenho */}
        {analyticsData.performance_over_time && analyticsData.performance_over_time.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <FaChartLine className="text-green-600" />
              <span>Histórico de Desempenho (Últimos 30 dias)</span>
            </h2>
            <div className="space-y-4">
              {analyticsData.performance_over_time.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {getGradeIcon(item.grade)}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{item.homework_title}</h3>
                      <p className="text-sm text-gray-600">{item.subject}</p>
                      <p className="text-xs text-gray-500">{formatDate(item.date)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-bold ${getGradeColor(item.grade)}`}>
                      {item.grade}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gráfico Visual de Desempenho */}
        {analyticsData.performance_over_time && analyticsData.performance_over_time.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <FaChartLine className="text-purple-600" />
              <span>Gráfico de Evolução</span>
            </h2>
            <div className="relative h-64">
              <div className="absolute inset-0 flex items-end justify-between space-x-2">
                {analyticsData.performance_over_time.map((item, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div 
                      className={`w-full rounded-t ${
                        item.grade >= 9 ? 'bg-green-500' :
                        item.grade >= 7 ? 'bg-blue-500' :
                        item.grade >= 5 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ height: `${(item.grade / 10) * 100}%` }}
                    ></div>
                    <p className="text-xs text-gray-600 mt-1 text-center">
                      {formatDate(item.date)}
                    </p>
                    <p className={`text-sm font-bold ${getGradeColor(item.grade)}`}>
                      {item.grade}
                    </p>
                  </div>
                ))}
              </div>
              {/* Linhas de grade */}
              <div className="absolute inset-0 flex flex-col justify-between">
                {[10, 8, 6, 4, 2, 0].map((value) => (
                  <div key={value} className="flex items-center">
                    <span className="text-xs text-gray-400 w-6">{value}</span>
                    <div className="flex-1 border-t border-gray-200"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Resumo de Insights */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
            <FaUsers className="text-orange-600" />
            <span>Resumo de Insights</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Pontos Fortes</h3>
              <div className="space-y-2">
                {analyticsData.punctuality_rate >= 80 && (
                  <div className="flex items-center space-x-2 text-green-600">
                    <FaCheckCircle className="w-4 h-4" />
                    <span className="text-sm">Excelente pontualidade nas entregas</span>
                  </div>
                )}
                {analyticsData.current_month_average >= 8 && (
                  <div className="flex items-center space-x-2 text-green-600">
                    <FaCheckCircle className="w-4 h-4" />
                    <span className="text-sm">Média geral acima de 8.0</span>
                  </div>
                )}
                {analyticsData.improvement > 0 && (
                  <div className="flex items-center space-x-2 text-green-600">
                    <FaCheckCircle className="w-4 h-4" />
                    <span className="text-sm">Desempenho em evolução positiva</span>
                  </div>
                )}
                {Object.values(analyticsData.performance_by_subject || {}).some(subject => subject.average >= 9) && (
                  <div className="flex items-center space-x-2 text-green-600">
                    <FaCheckCircle className="w-4 h-4" />
                    <span className="text-sm">Excelência em algumas matérias</span>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Áreas de Melhoria</h3>
              <div className="space-y-2">
                {analyticsData.punctuality_rate < 80 && (
                  <div className="flex items-center space-x-2 text-yellow-600">
                    <FaClock className="w-4 h-4" />
                    <span className="text-sm">Melhorar pontualidade nas entregas</span>
                  </div>
                )}
                {analyticsData.current_month_average < 7 && (
                  <div className="flex items-center space-x-2 text-yellow-600">
                    <FaBullseye className="w-4 h-4" />
                    <span className="text-sm">Focar em melhorar a média geral</span>
                  </div>
                )}
                {analyticsData.improvement < 0 && (
                  <div className="flex items-center space-x-2 text-red-600">
                    <FaExclamationTriangle className="w-4 h-4" />
                    <span className="text-sm">Reverter tendência de declínio</span>
                  </div>
                )}
                {Object.entries(analyticsData.performance_by_subject || {}).some(([subject, data]) => data.average < 6) && (
                  <div className="flex items-center space-x-2 text-red-600">
                    <FaExclamationTriangle className="w-4 h-4" />
                    <span className="text-sm">Atenção especial em algumas matérias</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 